/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {WinPresentationController} from "../../WinPresentationController";
import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {Serial} from "../../../Serial";
import {SerialGameConfig} from "../../../SerialGameConfig";
import {IParsedBetWayWinData} from "../../../server/data/SerialParsedGameData";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {InitialWinView} from "./InitialWinView";
import {SerialSound} from "../../../sound/SerialSound";
import {IBetWayWinData} from "../../../server/data/ISerialData";

export class InitialWinController extends WinPresentationController {

    private _mainTimeline: gsap.core.Timeline | null;
    private _view: InitialWinView;

    private _belowStakeWinRestriction: boolean;
    private _winLevel: number;
    private _lastWinSym: number[];

    constructor() {
        super("InitialWinController", true);
        this._view = new InitialWinView();
    }

    protected hasWin(): boolean {
        const data = Serial.model.parsedGameData;
        return data.betWins?.length > 0 && !data.nextMode.includes("RESPIN");
    }

    protected async createWinAnimation(): Promise<any> {
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        this._winLevel = this.getEffectLevel(Serial.model.parsedGameData.currentWinRatioConfig?.ratio!);
        const tl: gsap.core.Timeline = gsap.timeline();
        const betWaysWinData: IParsedBetWayWinData[] = Serial.model.parsedGameData.betWins;
        if (Serial.apiPlugin.gameClientConfiguration.displayIndividualWinsInGame) {
            for (let betWayIdx: number = 0; betWayIdx < betWaysWinData.length; betWayIdx++) {
                let lastSymPos: number[] = [0, 0];
                const indiBetWayWinData: IParsedBetWayWinData = betWaysWinData[betWayIdx],
                    indiTL: gsap.core.Timeline = gsap.timeline(),
                    highlightDurr: number = 0.4;
                for (let reelIdx: number = 0; reelIdx < indiBetWayWinData.reelWinPositions.length; reelIdx++) {
                    for (let symIdx: number = 0; symIdx < indiBetWayWinData.reelWinPositions[reelIdx].length; symIdx++) {
                        if (indiBetWayWinData.reelWinPositions[reelIdx][symIdx]) {
                            indiTL.add(() => {
                                Serial.winHighlightController.playWinHighlightAnimation(Serial.getSymbol(reelIdx, symIdx, true), false);
                            });
                            lastSymPos = [reelIdx, symIdx];
                        }
                    }
                }
                if (!BelowStakeWinRestriction.isWinBelowStake()) {
                    indiTL.add(Serial.getSymbol(lastSymPos[0], lastSymPos[1], true).showWinAndWays(highlightDurr, indiBetWayWinData.multipliedAmount, indiBetWayWinData.numberOfCombinations), 0);
                }
                this._lastWinSym = lastSymPos;
                tl.add(indiTL);
                tl.to(this, {duration: 0.4});
            }
        }
        tl.add([
            this.getWinAnimation(),
            this.getWinCountUpAnimation()
        ]);
        this._mainTimeline = tl;
        if (tl.totalDuration() > 0) {
            await Promise.resolve(tl);
        }
        this._mainTimeline = null;
    }

    protected getWinAnimation(): gsap.core.Timeline {
        const data = Serial.model.parsedGameData;
        const tl = gsap.timeline();
        let winDelay = 0;
        let winWaysWon = 0;
        if (!this._belowStakeWinRestriction) {
            if (data.currentWinRatioConfig!.bigWinLevel > -1) {
                SerialSound.soundPlayer.playTrack({name: SerialSound.PRE_BIG_WIN});
                SerialSound.fadeMusic(0, 1);
            } else {
                SerialSound.soundPlayer.playTrack({name: SerialSound.WIN_SOUNDS[this._winLevel]});
            }

        }
        data.betWins.forEach(w => {
            winWaysWon += (<IBetWayWinData>w).numberOfCombinations;
            tl.add(this.getSingleBetWayWinAnimation(w, winWaysWon), winDelay);
            // winDelay += (winLevel < 3 ? 0 : 0.5);
        });
        return tl;
    }

    protected getSingleBetWayWinAnimation(winData: IParsedBetWayWinData, winWaysWon: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        let symbolDelay = 0;
        const reelDelay = this.getReelDelay(this._winLevel);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let hasReelWin = false;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    tl.add(this.getSymbolWinAnimation(reelId, symId), symbolDelay);
                }
            });
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });

        if (!this._belowStakeWinRestriction) {
            tl.add(Serial.waysWon.showWinWaysWon(winWaysWon, Serial.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1), 0);
        }

        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number): gsap.core.Timeline {
        const tl = gsap.timeline();
        const sym = Serial.getSymbol(reelId, symId, false);
        const position = sym.getGlobalPosition();
        tl.add(() => Serial.getReelView(reelId, symId).addWinSymbol(sym));
        if (!this._belowStakeWinRestriction) {
            tl.add(this._view.playAnimation(sym.symbolHeight, position, this._winLevel), 0);
        }

        return tl;
    }

    protected getWinCountUpAnimation(): gsap.core.Timeline {
        const data = Serial.model.parsedGameData;
        const tl = gsap.timeline();
        if (data.currentWinRatioConfig!.bigWinLevel > -1) {
            return tl; // no count up for big win.
        }
        if (data.nextMode.includes("RESPIN")) {
            return tl;
        }

        if (!this._belowStakeWinRestriction) {
            tl.add(Serial.initialCountUp.getCountUpAnimation(0, data.totalSpinWinnings, data.currentWinRatioConfig!.duration));
        }
        tl.add(() => {
            if (!this._belowStakeWinRestriction) {
                const soundName = SerialSound.WIN_END_SOUNDS[this._winLevel];
                if (soundName.length > 0) {
                    SerialSound.soundPlayer.playTrack({name: soundName});
                }
            }
            SlotGame.keypadPlugin.setWin(data.totalWin, data.showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
        });

        return tl;
    }

    protected skip() {
        if (this._lastWinSym?.length > 0) {
            Serial.getSymbol(this._lastWinSym[0], this._lastWinSym[1]).individualWinWaysAmountTL.forEach((tween) => {
                tween.progress(1);
            })
        }
        this._mainTimeline?.progress(1);

        if (Serial.model.parsedGameData.currentWinRatioConfig!.bigWinLevel > -1) {
            SerialSound.soundPlayer.stopTrack(SerialSound.PRE_BIG_WIN);
        } else {
            SerialSound.soundPlayer.stopTrack(SerialSound.WIN_SOUNDS[this._winLevel]);
        }
    }

    private getEffectLevel(winRatio: number): number {
        for (let i = 1; i < 4; i++) {
            if (winRatio < SerialGameConfig.WIN_RATIOS[i].ratio) {
                return i - 1;
            }
        }
        return 3;
    }

    private getReelDelay(winLevel: number): number {
        // tiny/small
        if (winLevel < 2) {
            return 0;
        }

        //mid
        if (winLevel < 3) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }
}